import { useCallback, useEffect, useMemo, useState } from 'react';
import { pgApi } from '../../utils/pgApi';
import LoadingMessage from '../common/LoadingMessage';
import ErrorMessage from '../common/ErrorMessage';
import { useToast } from '../common/ToastProvider';
import EntityModal from './EntityModal';
import { useAuth } from '../../auth/AuthContext';

const TABS = [
    { id: 'overview', label: 'Overview' },
    { id: 'locations', label: 'Locations & Rooms' },
    { id: 'networking', label: 'Networking' },
    { id: 'devices', label: 'Devices' },
];

const NETWORK_TYPE_OPTIONS = [
    { value: 'vdsl', label: 'VDSL' },
    { value: 'cable', label: 'Cable' },
    { value: 'fiber', label: 'Fiber' },
];

const MODEM_TYPE_OPTIONS = [
    { value: 'DSL', label: 'DSL' },
    { value: 'Cable', label: 'Cable' },
    { value: 'Fibre', label: 'Fibre' },
];

const CAPABILITY_OPTIONS = [
    { value: 'b', label: '802.11b' },
    { value: 'g', label: '802.11g' },
    { value: 'n', label: '802.11n' },
    { value: 'ac', label: '802.11ac' },
    { value: 'ax', label: '802.11ax' },
];

const FREQUENCY_OPTIONS = [
    { value: 24, label: '2.4 GHz' },
    { value: 50, label: '5 GHz' },
];

const groupByArray = (items, key) =>
    items.reduce((acc, item) => {
        const value = item?.[key];
        if (value === undefined || value === null) {
            return acc;
        }
        if (!acc[value]) {
            acc[value] = [];
        }
        acc[value].push(item);
        return acc;
    }, {});

const indexBy = (items, key) =>
    items.reduce((acc, item) => {
        const value = item?.[key];
        if (value === undefined || value === null) {
            return acc;
        }
        acc[value] = item;
        return acc;
    }, {});

const formatDate = (value) => {
    if (!value) {
        return '—';
    }
    try {
        return new Date(value).toLocaleString();
    } catch (error) {
        return String(value);
    }
};

function SummaryCard({ title, value, subtitle }) {
    return (
        <div
            style={{
                border: '1px solid var(--color-glass-border)',
                borderRadius: '16px',
                padding: '1.2rem',
                background: 'var(--color-surface-alt)',
                boxShadow: 'var(--shadow-soft)',
                color: 'var(--color-text)',
            }}
        >
            <strong
                style={{
                    display: 'block',
                    marginBottom: '0.4rem',
                    fontFamily: 'var(--font-display)',
                    letterSpacing: '0.18em',
                    textTransform: 'uppercase',
                    fontSize: '0.78rem',
                    color: 'var(--color-text-muted)',
                }}
            >
                {title}
            </strong>
            <span
                style={{
                    fontSize: '1.8rem',
                    fontWeight: 600,
                    letterSpacing: '0.08em',
                }}
            >
                {value}
            </span>
            {subtitle ? (
                <div
                    style={{
                        color: 'var(--color-text-muted)',
                        marginTop: '0.5rem',
                        fontSize: '0.9rem',
                    }}
                >
                    {subtitle}
                </div>
            ) : null}
        </div>
    );
}

const PANEL_BASE_STYLE = {
    border: '1px solid var(--color-glass-border)',
    borderRadius: '20px',
    background: 'var(--color-surface-alt)',
    boxShadow: 'var(--shadow-soft)',
    color: 'var(--color-text)',
};

const PANEL_WITH_PADDING = {
    ...PANEL_BASE_STYLE,
    padding: '1.25rem',
};

const TABLE_CONTAINER_STYLE = {
    ...PANEL_BASE_STYLE,
    overflow: 'hidden',
};

const BADGE_SURFACE_STYLE = {
    borderRadius: '18px',
    padding: '1rem',
    border: '1px solid var(--color-glass-border)',
    background: 'var(--color-surface)',
    boxShadow: 'var(--shadow-soft)',
    color: 'var(--color-text)',
};

const DANGER_ACTION_STYLE = {
    background: 'color-mix(in srgb, var(--color-danger) 18%, transparent)',
    color: 'var(--color-danger)',
    borderColor: 'color-mix(in srgb, var(--color-danger) 45%, transparent)',
};

const TAB_ACTIVE_BACKGROUND =
    'linear-gradient(135deg, color-mix(in srgb, var(--color-primary) 45%, transparent), color-mix(in srgb, var(--color-secondary) 28%, transparent))';

const TAB_INACTIVE_BACKGROUND = 'var(--color-surface)';

const STATUS_SUCCESS_BACKGROUND = 'color-mix(in srgb, var(--color-success) 22%, transparent)';
const STATUS_DANGER_BACKGROUND = 'color-mix(in srgb, var(--color-danger) 20%, transparent)';
const STATUS_ACCENT_BACKGROUND = 'color-mix(in srgb, var(--color-accent) 24%, transparent)';

export function PgUsersDashboard() {
    const { hasPermission, user: currentUser } = useAuth();
    const canListAll = hasPermission('users:read');
    const canEdit = hasPermission('users:write');
    const canViewDeployments = hasPermission('deployments:read');
    const canViewDevices = hasPermission('devices:list') || hasPermission('devices:read');

    const [data, setData] = useState({
        users: [],
        locations: [],
        floors: [],
        rooms: [],
        billingAddresses: [],
        ispDetails: [],
        routers: [],
        accessPoints: [],
        radios: [],
        devices: [],
        provisions: [],
        deployments: [],
    });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedUserId, setSelectedUserId] = useState(null);
    const [activeTab, setActiveTab] = useState(TABS[0].id);
    const [modalConfig, setModalConfig] = useState(null);
    const { addToast } = useToast();

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            // If user cannot list all users, fetch only self
            const usersPromise = canListAll 
                ? pgApi.listUsers() 
                : pgApi.getUser(currentUser.id).then(u => [u]);

            // Helper to conditionally execute fetch functions to avoid eager evaluation
            // Also enforces array return type and handles errors gracefully
            const fetchIf = (condition, fn, fallbackFn) => {
                const promise = condition ? fn() : (fallbackFn ? fallbackFn() : Promise.resolve([]));
                return promise
                    .then(data => Array.isArray(data) ? data : [])
                    .catch(err => {
                        console.error('Fetch failed in fetchIf:', err);
                        return [];
                    });
            };

            const [
                users,
                locations,
                floors,
                rooms,
                billingAddresses,
                ispDetails,
                routers,
                accessPoints,
                radios,
                devices,
                provisions,
                deployments,
            ] = await Promise.all([
                usersPromise,
                fetchIf(canViewDeployments, () => pgApi.listLocations(), () => pgApi.listUserLocations(currentUser.id)),
                fetchIf(canViewDeployments, () => pgApi.listFloors(), () => pgApi.listUserFloors(currentUser.id)),
                fetchIf(canViewDeployments, () => pgApi.listRooms(), () => pgApi.listUserRooms(currentUser.id)),
                fetchIf(canListAll, () => pgApi.listBillingAddresses(), () => pgApi.listUserBillingAddresses(currentUser.id)),
                fetchIf(canViewDeployments, () => pgApi.listIspDetails(), () => pgApi.listUserIspDetails(currentUser.id)),
                fetchIf(canViewDeployments, () => pgApi.listRouters(), () => pgApi.listUserRouters(currentUser.id)),
                fetchIf(canViewDeployments, () => pgApi.listAccessPoints(), () => pgApi.listUserAccessPoints(currentUser.id)),
                fetchIf(canViewDeployments, () => pgApi.listRadios(), () => pgApi.listUserRadios(currentUser.id)),
                fetchIf(canViewDevices, () => pgApi.listDevices(), () => pgApi.listUserDevices(currentUser.id)),
                fetchIf(canViewDevices, () => pgApi.listProvisions(), () => pgApi.listUserProvisions(currentUser.id)),
                fetchIf(canViewDeployments, () => pgApi.listDeployments(), () => pgApi.listUserDeployments(currentUser.id)),
            ]);

            setData({
                users,
                locations,
                floors,
                rooms,
                billingAddresses,
                ispDetails,
                routers,
                accessPoints,
                radios,
                devices,
                provisions,
                deployments,
            });

            setSelectedUserId((current) => {
                if (current && users.some((user) => user.id === current)) {
                    return current;
                }
                return users[0]?.id ?? null;
            });

            setError(null);
        } catch (err) {
            setError(err.message || 'Failed to load data');
        } finally {
            setLoading(false);
        }
    }, [canListAll, canViewDeployments, canViewDevices, currentUser.id]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const locationsByUser = useMemo(() => groupByArray(data.locations, 'user_id'), [data.locations]);
    const floorsByLocation = useMemo(() => groupByArray(data.floors, 'location_id'), [data.floors]);
    const roomsByFloor = useMemo(() => groupByArray(data.rooms, 'location_floor_id'), [data.rooms]);
    const billingByUser = useMemo(() => indexBy(data.billingAddresses, 'user_id'), [data.billingAddresses]);
    const ispByUser = useMemo(() => indexBy(data.ispDetails, 'user_id'), [data.ispDetails]);
    const routerByUser = useMemo(() => indexBy(data.routers, 'user_id'), [data.routers]);
    const apsByUser = useMemo(() => groupByArray(data.accessPoints, 'user_id'), [data.accessPoints]);
    const radiosByAp = useMemo(() => groupByArray(data.radios, 'access_point_id'), [data.radios]);
    const devicesByUser = useMemo(() => groupByArray(data.devices, 'user_id'), [data.devices]);
    const provisionsByDevice = useMemo(() => indexBy(data.provisions, 'device_id'), [data.provisions]);
    const deploymentsByDevice = useMemo(() => indexBy(data.deployments, 'device_id'), [data.deployments]);

    const selectedUser = useMemo(
        () => data.users.find((user) => user.id === selectedUserId) ?? null,
        [data.users, selectedUserId]
    );

    const isOwnProfile = selectedUser?.id === currentUser?.id;
    // Allow editing if user has global write permission OR if they are editing their own profile
    const canManage = canEdit || isOwnProfile;

    const selectedLocations = useMemo(
        () => (selectedUser ? locationsByUser[selectedUser.id] ?? [] : []),
        [locationsByUser, selectedUser]
    );
    const selectedAps = useMemo(
        () => (selectedUser ? apsByUser[selectedUser.id] ?? [] : []),
        [apsByUser, selectedUser]
    );
    const selectedDevices = useMemo(
        () => (selectedUser ? devicesByUser[selectedUser.id] ?? [] : []),
        [devicesByUser, selectedUser]
    );
    const selectedRouter = useMemo(
        () => (selectedUser ? routerByUser[selectedUser.id] ?? null : null),
        [routerByUser, selectedUser]
    );
    const selectedIspDetail = useMemo(
        () => (selectedUser ? ispByUser[selectedUser.id] ?? null : null),
        [ispByUser, selectedUser]
    );
    const selectedBilling = useMemo(
        () => (selectedUser ? billingByUser[selectedUser.id] ?? null : null),
        [billingByUser, selectedUser]
    );

    const allUserRooms = useMemo(() => {
        if (!selectedUser) {
            return [];
        }
        return selectedLocations.flatMap((location) => {
            const floors = floorsByLocation[location.id] ?? [];
            return floors.flatMap((floor) => roomsByFloor[floor.id] ?? []);
        });
    }, [floorsByLocation, roomsByFloor, selectedLocations, selectedUser]);

    const closeModal = useCallback(() => {
        setModalConfig(null);
    }, []);

    const openModal = useCallback((config) => {
        setModalConfig({ ...config, open: true });
    }, []);

    const handleDelete = useCallback(
        async (action, successMessage) => {
            // Check if we can edit generally or if this is my own data
            if (!canManage) {
                return;
            }
            try {
                await action();
                await fetchData();
                addToast({ type: 'success', title: successMessage });
            } catch (err) {
                addToast({ type: 'error', title: 'Operation failed', description: err.message || 'Unknown error' });
            }
        },
        [addToast, canManage, fetchData]
    );

    const showUserModal = (user) => {
        // Allow if admin OR if editing self
        const isSelf = user && user.id === currentUser.id;
        if (!canEdit && !isSelf) {
            return;
        }

        openModal({
            title: user ? `Edit User #${user.id}` : 'Add User',
            initialValues: user
                ? {
                      firstname: user.firstname,
                      lastname: user.lastname,
                      email: user.email,
                      password: '',
                      is_active: user.is_active,
                      last_login: user.last_login,
                  }
                : { is_active: true, password: '' },
            fields: [
                { name: 'firstname', label: 'First name', required: true },
                { name: 'lastname', label: 'Last name', required: true },
                { name: 'email', label: 'Email', type: 'email', required: true },
                {
                    name: 'password',
                    label: 'Password',
                    type: 'password',
                    required: !user,
                    helperText: user ? 'Leave blank to keep the current password' : undefined,
                },
                { name: 'is_active', label: 'Active', type: 'checkbox', disabled: !canEdit },
                { name: 'last_login', label: 'Last login', type: 'datetime-local', includeNull: true, disabled: !canEdit },
            ],
            onSubmit: async (payload) => {
                const body = { ...payload };
                if (!body.password) {
                    delete body.password;
                }
                if (user) {
                    await pgApi.updateUser(user.id, body);
                    addToast({ type: 'success', title: 'User updated' });
                } else {
                    await pgApi.createUser(body);
                    addToast({ type: 'success', title: 'User created' });
                }
                await fetchData();
            },
        });
    };

    const showLocationModal = (location) => {
        if (!canManage) {
            return;
        }
        if (!selectedUser) {
            return;
        }
        openModal({
            title: location ? `Edit Location #${location.id}` : `Add Location for ${selectedUser.firstname}`,
            initialValues: location
                ? {
                      description: location.description,
                      user_id: location.user_id,
                      house_number: location.house_number,
                      street: location.street,
                      city: location.city,
                      postcode: location.postcode,
                      country: location.country,
                      lat: location.lat ?? '',
                      lng: location.lng ?? '',
                  }
                : { user_id: selectedUser.id, country: 'GB' },
            fields: [
                { name: 'user_id', label: 'User ID', type: 'number', required: true, readOnly: true },
                { name: 'description', label: 'Description', required: true },
                { name: 'house_number', label: 'House number', type: 'number', required: true },
                { name: 'street', label: 'Street', required: true },
                { name: 'city', label: 'City', required: true },
                { name: 'postcode', label: 'Postcode', required: true },
                { name: 'country', label: 'Country (ISO 2)', required: true },
                { name: 'lat', label: 'Latitude', type: 'number', numberType: 'float', step: '0.000001' },
                { name: 'lng', label: 'Longitude', type: 'number', numberType: 'float', step: '0.000001' },
            ],
            onSubmit: async (payload) => {
                const body = {
                    ...payload,
                    user_id: selectedUser.id,
                };
                if (location) {
                    if (canEdit) {
                        await pgApi.updateLocation(location.id, body);
                    } else {
                        await pgApi.updateUserLocation(selectedUser.id, location.id, body);
                    }
                    addToast({ type: 'success', title: 'Location updated' });
                } else {
                    if (canEdit) {
                        await pgApi.createLocation(body);
                    } else {
                        await pgApi.createUserLocation(selectedUser.id, body);
                    }
                    addToast({ type: 'success', title: 'Location created' });
                }
                await fetchData();
            },
        });
    };

    const showFloorModal = (floor, defaultLocationId) => {
        if (!canManage) {
            return;
        }
        if (!selectedUser) {
            return;
        }
        const locationOptions = selectedLocations.map((loc) => ({ value: loc.id, label: `${loc.description} (#${loc.id})` }));
        openModal({
            title: floor ? `Edit Floor #${floor.id}` : 'Add Floor',
            initialValues: floor
                ? {
                      location_id: floor.location_id,
                      floor_name: floor.floor_name,
                      physical_level: floor.physical_level ?? '',
                  }
                : { location_id: defaultLocationId ?? locationOptions[0]?.value },
            fields: [
                {
                    name: 'location_id',
                    label: 'Location',
                    type: 'select',
                    required: true,
                    options: locationOptions,
                },
                { name: 'floor_name', label: 'Floor name', required: true },
                {
                    name: 'physical_level',
                    label: 'Physical level',
                    type: 'number',
                    helperText: 'Optional integer (e.g., ground=0, basement=-1)',
                },
            ],
            onSubmit: async (payload) => {
                if (floor) {
                    if (canEdit) {
                        await pgApi.updateFloor(floor.id, payload);
                    } else {
                        await pgApi.updateUserFloor(selectedUser.id, floor.id, payload);
                    }
                    addToast({ type: 'success', title: 'Floor updated' });
                } else {
                    if (canEdit) {
                        await pgApi.createFloor(payload);
                    } else {
                        await pgApi.createUserFloor(selectedUser.id, payload);
                    }
                    addToast({ type: 'success', title: 'Floor created' });
                }
                await fetchData();
            },
        });
    };

    const showRoomModal = (room, floorId) => {
        if (!canManage) {
            return;
        }
        if (!selectedUser) {
            return;
        }

        const floorOptions = selectedLocations.flatMap((loc) => {
            const floors = floorsByLocation[loc.id] ?? [];
            return floors.map((floor) => ({ value: floor.id, label: `${loc.description} – ${floor.floor_name}` }));
        });

        openModal({
            title: room ? `Edit Room #${room.id}` : 'Add Room',
            initialValues: room
                ? {
                      location_floor_id: room.location_floor_id,
                      room_name: room.room_name,
                      description: room.description ?? '',
                  }
                : { location_floor_id: floorId ?? floorOptions[0]?.value },
            fields: [
                {
                    name: 'location_floor_id',
                    label: 'Floor',
                    type: 'select',
                    required: true,
                    options: floorOptions,
                },
                { name: 'room_name', label: 'Room name', required: true },
                { name: 'description', label: 'Description', type: 'textarea', rows: 2 },
            ],
            onSubmit: async (payload) => {
                if (room) {
                    if (canEdit) {
                        await pgApi.updateRoom(room.id, payload);
                    } else {
                        await pgApi.updateUserRoom(selectedUser.id, room.id, payload);
                    }
                    addToast({ type: 'success', title: 'Room updated' });
                } else {
                    if (canEdit) {
                        await pgApi.createRoom(payload);
                    } else {
                        await pgApi.createUserRoom(selectedUser.id, payload);
                    }
                    addToast({ type: 'success', title: 'Room created' });
                }
                await fetchData();
            },
        });
    };

    const showBillingModal = () => {
        if (!canManage) {
            return;
        }
        if (!selectedUser) {
            return;
        }
        openModal({
            title: selectedBilling ? 'Edit Billing Address' : 'Add Billing Address',
            initialValues: selectedBilling
                ? {
                      description: selectedBilling.description,
                      house_number: selectedBilling.house_number,
                      street: selectedBilling.street,
                      city: selectedBilling.city,
                      county: selectedBilling.county,
                      postcode: selectedBilling.postcode,
                      country: selectedBilling.country,
                  }
                : { country: 'GB' },
            fields: [
                { name: 'description', label: 'Description', required: true },
                { name: 'house_number', label: 'House number', type: 'number', required: true },
                { name: 'street', label: 'Street', required: true },
                { name: 'city', label: 'City', required: true },
                { name: 'county', label: 'County', required: true },
                { name: 'postcode', label: 'Postcode', required: true },
                { name: 'country', label: 'Country (ISO 2)', required: true },
            ],
            onSubmit: async (payload) => {
                const body = { ...payload, user_id: selectedUser.id };
                if (selectedBilling) {
                    if (canEdit) {
                        await pgApi.updateBillingAddress(selectedBilling.id, body);
                    } else {
                        await pgApi.updateUserBillingAddress(selectedUser.id, selectedBilling.id, body);
                    }
                    addToast({ type: 'success', title: 'Billing address updated' });
                } else {
                    if (canEdit) {
                        await pgApi.createBillingAddress(body);
                    } else {
                        await pgApi.createUserBillingAddress(selectedUser.id, body);
                    }
                    addToast({ type: 'success', title: 'Billing address created' });
                }
                await fetchData();
            },
        });
    };

    const showIspModal = () => {
        if (!canManage) {
            return;
        }
        if (!selectedUser) {
            return;
        }
        openModal({
            title: selectedIspDetail ? 'Edit ISP Detail' : 'Add ISP Detail',
            initialValues: selectedIspDetail
                ? {
                      provider: selectedIspDetail.provider,
                      type: selectedIspDetail.type,
                  }
                : {},
            fields: [
                { name: 'provider', label: 'Provider', required: true },
                {
                    name: 'type',
                    label: 'Connection type',
                    type: 'select',
                    required: true,
                    options: NETWORK_TYPE_OPTIONS,
                },
            ],
            onSubmit: async (payload) => {
                const body = { ...payload, user_id: selectedUser.id };
                if (selectedIspDetail) {
                    if (canEdit) {
                        await pgApi.updateIspDetail(selectedIspDetail.id, body);
                    } else {
                        await pgApi.updateUserIspDetail(selectedUser.id, selectedIspDetail.id, body);
                    }
                    addToast({ type: 'success', title: 'ISP detail updated' });
                } else {
                    if (canEdit) {
                        await pgApi.createIspDetail(body);
                    } else {
                        await pgApi.createUserIspDetail(selectedUser.id, body);
                    }
                    addToast({ type: 'success', title: 'ISP detail created' });
                }
                await fetchData();
            },
        });
    };

    const showRouterModal = (router) => {
        if (!canManage) {
            return;
        }
        if (!selectedUser) {
            return;
        }

        const roomOptions = allUserRooms.map((room) => ({ value: room.id, label: room.room_name }));
        const ispOptions = selectedIspDetail ? [{ value: selectedIspDetail.id, label: `${selectedIspDetail.provider} (#${selectedIspDetail.id})` }] : [];

        openModal({
            title: router ? `Edit Router #${router.id}` : 'Add Router',
            initialValues: router
                ? {
                      location_room_id: router.location_room_id,
                      isp_id: router.isp_id ?? '',
                      public_ip: router.public_ip,
                      gateway_ip: router.gateway_ip,
                      modem_type: router.modem_type,
                  }
                : {
                      isp_id: selectedIspDetail?.id ?? '',
                  },
            fields: [
                {
                    name: 'location_room_id',
                    label: 'Room',
                    type: 'select',
                    required: true,
                    options: roomOptions,
                },
                {
                    name: 'isp_id',
                    label: 'ISP Detail',
                    type: 'select',
                    allowEmpty: true,
                    options: ispOptions,
                    helperText: selectedIspDetail ? 'Optional link to ISP detail' : 'Add ISP detail first',
                },
                { name: 'public_ip', label: 'Public IP', required: true },
                { name: 'gateway_ip', label: 'Gateway IP', required: true },
                {
                    name: 'modem_type',
                    label: 'Modem type',
                    type: 'select',
                    required: true,
                    options: MODEM_TYPE_OPTIONS,
                },
            ],
            onSubmit: async (payload) => {
                const body = { ...payload, user_id: selectedUser.id };
                if (!body.isp_id) {
                    delete body.isp_id;
                }
                if (router) {
                    if (canEdit) {
                        await pgApi.updateRouter(router.id, body);
                    } else {
                        await pgApi.updateUserRouter(selectedUser.id, router.id, body);
                    }
                    addToast({ type: 'success', title: 'Router updated' });
                } else {
                    if (canEdit) {
                        await pgApi.createRouter(body);
                    } else {
                        await pgApi.createUserRouter(selectedUser.id, body);
                    }
                    addToast({ type: 'success', title: 'Router created' });
                }
                await fetchData();
            },
        });
    };

    const showAccessPointModal = (accessPoint) => {
        if (!canManage) {
            return;
        }
        if (!selectedUser) {
            return;
        }
        const routerOptions = selectedRouter
            ? [{ value: selectedRouter.id, label: `Router #${selectedRouter.id}` }]
            : [];
        const roomOptions = allUserRooms.map((room) => ({ value: room.id, label: room.room_name }));

        openModal({
            title: accessPoint ? `Edit Access Point #${accessPoint.id}` : 'Add Access Point',
            initialValues: accessPoint
                ? {
                      router_id: accessPoint.router_id,
                      location_room_id: accessPoint.location_room_id,
                  }
                : {
                      router_id: selectedRouter?.id ?? '',
                  },
            fields: [
                {
                    name: 'router_id',
                    label: 'Router',
                    type: 'select',
                    required: true,
                    options: routerOptions,
                    helperText: routerOptions.length === 0 ? 'Add a router first' : undefined,
                },
                {
                    name: 'location_room_id',
                    label: 'Room',
                    type: 'select',
                    required: true,
                    options: roomOptions,
                },
            ],
            onSubmit: async (payload) => {
                const body = { ...payload, user_id: selectedUser.id };
                if (accessPoint) {
                    if (canEdit) {
                        await pgApi.updateAccessPoint(accessPoint.id, body);
                    } else {
                        await pgApi.updateUserAccessPoint(selectedUser.id, accessPoint.id, body);
                    }
                    addToast({ type: 'success', title: 'Access point updated' });
                } else {
                    if (canEdit) {
                        await pgApi.createAccessPoint(body);
                    } else {
                        await pgApi.createUserAccessPoint(selectedUser.id, body);
                    }
                    addToast({ type: 'success', title: 'Access point created' });
                }
                await fetchData();
            },
        });
    };

    const showRadioModal = (accessPoint, radio) => {
        if (!canManage) {
            return;
        }
        openModal({
            title: radio ? `Edit Radio #${radio.id}` : `Add Radio for AP #${accessPoint.id}`,
            initialValues: radio
                ? {
                      access_point_id: radio.access_point_id,
                      bssid: radio.bssid,
                      ssid: radio.ssid,
                      capability: radio.capability,
                      frequency_band: radio.frequency_band,
                  }
                : { access_point_id: accessPoint.id, capability: 'ac', frequency_band: 50 },
            fields: [
                {
                    name: 'access_point_id',
                    label: 'Access Point ID',
                    type: 'number',
                    required: true,
                    readOnly: true,
                },
                { name: 'bssid', label: 'BSSID', required: true, helperText: '12 hex characters (e.g., AABBCCDDEEFF)' },
                { name: 'ssid', label: 'SSID', required: true },
                {
                    name: 'capability',
                    label: 'Capability',
                    type: 'select',
                    required: true,
                    options: CAPABILITY_OPTIONS,
                },
                {
                    name: 'frequency_band',
                    label: 'Frequency band',
                    type: 'select',
                    required: true,
                    options: FREQUENCY_OPTIONS,
                },
            ],
            onSubmit: async (payload) => {
                const body = { ...payload };
                if (radio) {
                    if (canEdit) {
                        await pgApi.updateRadio(radio.id, body);
                    } else {
                        await pgApi.updateUserRadio(selectedUser.id, radio.id, body);
                    }
                    addToast({ type: 'success', title: 'Radio updated' });
                } else {
                    if (canEdit) {
                        await pgApi.createRadio(body);
                    } else {
                        await pgApi.createUserRadio(selectedUser.id, body);
                    }
                    addToast({ type: 'success', title: 'Radio created' });
                }
                await fetchData();
            },
        });
    };

    const showDeviceModal = (device) => {
        if (!canManage) {
            return;
        }
        if (!selectedUser) {
            return;
        }
        const roomOptions = allUserRooms.map((room) => ({ value: room.id, label: room.room_name }));
        openModal({
            title: device ? `Edit Device #${device.id}` : 'Add Device',
            initialValues: device
                ? {
                      location_room_id: device.location_room_id,
                      mac: device.mac,
                      device_name: device.device_name,
                  }
                : {},
            fields: [
                {
                    name: 'location_room_id',
                    label: 'Room',
                    type: 'select',
                    required: true,
                    options: roomOptions,
                },
                { name: 'mac', label: 'MAC address', required: true },
                { name: 'device_name', label: 'Device name', required: true },
            ],
            onSubmit: async (payload) => {
                const body = { ...payload, user_id: selectedUser.id };
                if (device) {
                    if (canEdit) {
                        await pgApi.updateDevice(device.id, body);
                    } else {
                        await pgApi.updateUserDevice(selectedUser.id, device.id, body);
                    }
                    addToast({ type: 'success', title: 'Device updated' });
                } else {
                    if (canEdit) {
                        await pgApi.createDevice(body);
                    } else {
                        await pgApi.createUserDevice(selectedUser.id, body);
                    }
                    addToast({ type: 'success', title: 'Device created' });
                }
                await fetchData();
            },
        });
    };

    const showProvisionModal = (device, provision) => {
        if (!canManage) {
            return;
        }
        openModal({
            title: provision ? `Edit Provision for Device #${device.id}` : `Provision Device #${device.id}`,
            initialValues: provision
                ? {
                      device_id: provision.device_id,
                      is_provisioned: provision.is_provisioned,
                      provisioned_date: provision.provisioned_date,
                  }
                : { device_id: device.id, is_provisioned: true },
            fields: [
                { name: 'device_id', label: 'Device ID', type: 'number', required: true, readOnly: true },
                { name: 'is_provisioned', label: 'Provisioned', type: 'checkbox' },
                {
                    name: 'provisioned_date',
                    label: 'Provisioned date',
                    type: 'datetime-local',
                    includeNull: true,
                },
            ],
            onSubmit: async (payload) => {
                if (provision) {
                    if (canEdit) {
                        await pgApi.updateProvision(provision.id, payload);
                    } else {
                        await pgApi.updateUserProvision(selectedUser.id, provision.id, payload);
                    }
                    addToast({ type: 'success', title: 'Provision updated' });
                } else {
                    if (canEdit) {
                        await pgApi.createProvision(payload);
                    } else {
                        await pgApi.createUserProvision(selectedUser.id, payload);
                    }
                    addToast({ type: 'success', title: 'Provision created' });
                }
                await fetchData();
            },
        });
    };

    const showDeploymentModal = (device, deployment) => {
        if (!canManage) {
            return;
        }
        openModal({
            title: deployment ? `Edit Deployment for Device #${device.id}` : `Deploy Device #${device.id}`,
            initialValues: deployment
                ? {
                      device_id: deployment.device_id,
                      is_deployed: deployment.is_deployed,
                      start_date: deployment.start_date,
                      end_date: deployment.end_date,
                  }
                : { device_id: device.id, is_deployed: true },
            fields: [
                { name: 'device_id', label: 'Device ID', type: 'number', required: true, readOnly: true },
                { name: 'is_deployed', label: 'Deployed', type: 'checkbox' },
                { name: 'start_date', label: 'Start date', type: 'datetime-local', required: true },
                { name: 'end_date', label: 'End date', type: 'datetime-local', includeNull: true },
            ],
            onSubmit: async (payload) => {
                if (deployment) {
                    if (canEdit) {
                        await pgApi.updateDeployment(deployment.id, payload);
                    } else {
                        await pgApi.updateUserDeployment(selectedUser.id, deployment.id, payload);
                    }
                    addToast({ type: 'success', title: 'Deployment updated' });
                } else {
                    if (canEdit) {
                        await pgApi.createDeployment(payload);
                    } else {
                        await pgApi.createUserDeployment(selectedUser.id, payload);
                    }
                    addToast({ type: 'success', title: 'Deployment created' });
                }
                await fetchData();
            },
        });
    };

    if (loading) {
        return <LoadingMessage message="Loading users from /v1pg..." />;
    }

    if (error) {
        return <ErrorMessage message={error} />;
    }

    return (
        <div style={{ display: 'flex', gap: '1.5rem', alignItems: 'flex-start' }}>
            <div style={{ flex: '1 1 320px', maxWidth: '360px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
                    <h2 style={{ margin: 0 }}>Users</h2>
                    {canEdit ? (
                        <button type="button" className="primary-btn" onClick={() => showUserModal(null)}>
                            + Add User
                        </button>
                    ) : null}
                </div>
                <div style={TABLE_CONTAINER_STYLE}>
                    <table className="table" style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead>
                            <tr style={{ background: 'var(--color-surface)' }}>
                                <th style={{ textAlign: 'left', padding: '0.5rem' }}>Name</th>
                                <th style={{ textAlign: 'left', padding: '0.5rem' }}>Locations</th>
                                <th style={{ textAlign: 'left', padding: '0.5rem' }}>Devices</th>
                            </tr>
                        </thead>
                        <tbody>
                            {data.users.map((user) => {
                                const userLocations = locationsByUser[user.id] ?? [];
                                const userDevices = devicesByUser[user.id] ?? [];
                                return (
                                    <tr
                                        key={user.id}
                                        onClick={() => {
                                            setSelectedUserId(user.id);
                                            setActiveTab('overview');
                                        }}
                                        style={{
                                            cursor: 'pointer',
                                            background:
                                                user.id === selectedUserId
                                                    ? 'color-mix(in srgb, var(--color-primary) 16%, var(--color-surface-alt))'
                                                    : 'transparent',
                                        }}
                                    >
                                        <td style={{ padding: '0.5rem' }}>
                                            <div style={{ fontWeight: 600 }}>
                                                {user.firstname} {user.lastname}
                                            </div>
                                            <div style={{ fontSize: '0.85rem', color: 'var(--color-text-muted)' }}>{user.email}</div>
                                        </td>
                                        <td style={{ padding: '0.5rem' }}>{userLocations.length}</td>
                                        <td style={{ padding: '0.5rem' }}>{userDevices.length}</td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>

            <div style={{ flex: '2 1 0', minWidth: '0' }}>
                {selectedUser ? (
                    <div style={{ display: 'grid', gap: '1rem' }}>
                        <header style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <div>
                                <h2 style={{ margin: 0 }}>
                                    {selectedUser.firstname} {selectedUser.lastname}
                                </h2>
                                <div style={{ color: 'var(--color-text-muted)' }}>{selectedUser.email}</div>
                            </div>
                            <div style={{ display: 'flex', gap: '0.5rem' }}>
                                {(canEdit || selectedUser.id === currentUser.id) && (
                                    <button type="button" className="secondary-btn" onClick={() => showUserModal(selectedUser)}>
                                        Edit user
                                    </button>
                                )}
                                {canEdit && (
                                    <button
                                        type="button"
                                        className="secondary-btn"
                                        style={DANGER_ACTION_STYLE}
                                        onClick={() => {
                                            if (window.confirm('Delete this user and related records?')) {
                                                handleDelete(
                                                    () => pgApi.deleteUser(selectedUser.id),
                                                    'User deleted'
                                                );
                                            }
                                        }}
                                    >
                                        Delete user
                                    </button>
                                )}
                            </div>
                        </header>

                        <div style={{ display: 'flex', gap: '0.5rem' }}>
                            {TABS.map((tab) => (
                                <button
                                    key={tab.id}
                                    type="button"
                                    onClick={() => setActiveTab(tab.id)}
                                    style={{
                                        padding: '0.55rem 1.25rem',
                                        borderRadius: '999px',
                                        border: '1px solid var(--color-glass-border)',
                                        background: activeTab === tab.id ? TAB_ACTIVE_BACKGROUND : TAB_INACTIVE_BACKGROUND,
                                        color: activeTab === tab.id ? 'var(--color-primary)' : 'var(--color-text-muted)',
                                        cursor: 'pointer',
                                        letterSpacing: '0.14em',
                                        textTransform: 'uppercase',
                                        fontSize: '0.78rem',
                                        transition: 'transform var(--transition-snappy), box-shadow var(--transition-snappy)',
                                        boxShadow: activeTab === tab.id ? 'var(--shadow-glow)' : 'none',
                                    }}
                                >
                                    {tab.label}
                                </button>
                            ))}
                        </div>

                        {activeTab === 'overview' && (
                            <section style={{ display: 'grid', gap: '1rem' }}>
                                <div style={{ display: 'grid', gap: '1rem', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))' }}>
                                    <SummaryCard title="Locations" value={selectedLocations.length} />
                                    <SummaryCard title="Floors" value={selectedLocations.reduce((acc, loc) => acc + (floorsByLocation[loc.id]?.length ?? 0), 0)} />
                                    <SummaryCard title="Rooms" value={selectedLocations.reduce((acc, loc) => {
                                        const floors = floorsByLocation[loc.id] ?? [];
                                        const roomCount = floors.reduce((floorAcc, floor) => floorAcc + (roomsByFloor[floor.id]?.length ?? 0), 0);
                                        return acc + roomCount;
                                    }, 0)} />
                                    <SummaryCard title="Devices" value={selectedDevices.length} />
                                    <SummaryCard title="Access Points" value={selectedAps.length} />
                                </div>

                                <section style={PANEL_WITH_PADDING}>
                                    <header style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <h3 style={{ margin: 0 }}>ISP Detail</h3>
                                        <button type="button" className="secondary-btn" onClick={showIspModal}>
                                            {selectedIspDetail ? 'Edit ISP' : 'Add ISP'}
                                        </button>
                                    </header>
                                    {selectedIspDetail ? (
                                        <dl style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: '0.5rem' }}>
                                            <dt>Provider</dt>
                                            <dd>{selectedIspDetail.provider}</dd>
                                            <dt>Type</dt>
                                            <dd>{selectedIspDetail.type}</dd>
                                        </dl>
                                    ) : (
                                        <p style={{ color: 'var(--color-text-muted)' }}>No ISP detail on file.</p>
                                    )}
                                </section>

                                <section style={PANEL_WITH_PADDING}>
                                    <header style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <h3 style={{ margin: 0 }}>Billing Address</h3>
                                        <button type="button" className="secondary-btn" onClick={showBillingModal}>
                                            {selectedBilling ? 'Edit billing' : 'Add billing'}
                                        </button>
                                    </header>
                                    {selectedBilling ? (
                                        <dl style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: '0.5rem' }}>
                                            <dt>Address</dt>
                                            <dd>
                                                {selectedBilling.house_number} {selectedBilling.street}, {selectedBilling.city}
                                            </dd>
                                            <dt>County</dt>
                                            <dd>{selectedBilling.county}</dd>
                                            <dt>Postcode</dt>
                                            <dd>{selectedBilling.postcode}</dd>
                                            <dt>Country</dt>
                                            <dd>{selectedBilling.country}</dd>
                                        </dl>
                                    ) : (
                                        <p style={{ color: 'var(--color-text-muted)' }}>No billing address on file.</p>
                                    )}
                                </section>

                                <section style={PANEL_WITH_PADDING}>
                                    <h3 style={{ marginTop: 0 }}>Account Activity</h3>
                                    <dl style={{ display: 'grid', gridTemplateColumns: '160px 1fr', gap: '0.5rem' }}>
                                        <dt>Created</dt>
                                        <dd>{formatDate(selectedUser.created_at)}</dd>
                                        <dt>Last updated</dt>
                                        <dd>{formatDate(selectedUser.updated_at)}</dd>
                                        <dt>Last login</dt>
                                        <dd>{formatDate(selectedUser.last_login)}</dd>
                                        <dt>Status</dt>
                                        <dd>{selectedUser.is_active ? 'Active' : 'Inactive'}</dd>
                                    </dl>
                                </section>
                            </section>
                        )}

                        {activeTab === 'locations' && (
                            <section style={{ display: 'grid', gap: '1rem' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <h3 style={{ margin: 0 }}>Locations</h3>
                                    <button type="button" className="primary-btn" onClick={() => showLocationModal(null)}>
                                        + Add Location
                                    </button>
                                </div>

                                {selectedLocations.length === 0 ? (
                                    <p style={{ color: 'var(--color-text-muted)' }}>No locations for this user yet.</p>
                                ) : (
                                    <div style={{ display: 'grid', gap: '1rem' }}>
                                        {selectedLocations.map((location) => {
                                            const floors = floorsByLocation[location.id] ?? [];
                                            return (
                                                <article key={location.id} style={PANEL_WITH_PADDING}>
                                                    <header style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                        <div>
                                                            <h4 style={{ margin: 0 }}>{location.description}</h4>
                                                            <div style={{ color: 'var(--color-text-muted)', fontSize: '0.9rem' }}>
                                                                {location.house_number} {location.street}, {location.city} {location.postcode}
                                                            </div>
                                                        </div>
                                                        <div style={{ display: 'flex', gap: '0.5rem' }}>
                                                            <button
                                                                type="button"
                                                                className="secondary-btn"
                                                                onClick={() => showLocationModal(location)}
                                                            >
                                                                Edit
                                                            </button>
                                                            <button
                                                                type="button"
                                                                className="secondary-btn"
                                                                style={DANGER_ACTION_STYLE}
                                                                onClick={() => {
                                                                    if (window.confirm('Delete this location and nested rooms?')) {
                                                                        handleDelete(
                                                                            () =>
                                                                                canEdit
                                                                                    ? pgApi.deleteLocation(location.id)
                                                                                    : pgApi.deleteUserLocation(selectedUser.id, location.id),
                                                                            'Location deleted'
                                                                        );
                                                                    }
                                                                }}
                                                            >
                                                                Delete
                                                            </button>
                                                        </div>
                                                    </header>

                                                    <div style={{ marginTop: '1rem', display: 'flex', justifyContent: 'space-between' }}>
                                                        <strong>Floors ({floors.length})</strong>
                                                        <button
                                                            type="button"
                                                            className="secondary-btn"
                                                            onClick={() => showFloorModal(null, location.id)}
                                                        >
                                                            + Add Floor
                                                        </button>
                                                    </div>

                                                    {floors.length === 0 ? (
                                                        <p style={{ color: 'var(--color-text-muted)' }}>No floors defined.</p>
                                                    ) : (
                                                        <div style={{ display: 'grid', gap: '0.75rem', marginTop: '0.75rem' }}>
                                                            {floors.map((floor) => {
                                                                const rooms = roomsByFloor[floor.id] ?? [];
                                                                return (
                                                                    <section key={floor.id} style={BADGE_SURFACE_STYLE}>
                                                                        <header style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                                            <div>
                                                                                <strong>{floor.floor_name}</strong>
                                                                                {floor.physical_level !== null && floor.physical_level !== undefined ? (
                                                                                    <span style={{ color: 'var(--color-text-muted)', marginLeft: '0.5rem' }}>
                                                                                        (level {floor.physical_level})
                                                                                    </span>
                                                                                ) : null}
                                                                            </div>
                                                                            <div style={{ display: 'flex', gap: '0.5rem' }}>
                                                                                <button
                                                                                    type="button"
                                                                                    className="secondary-btn"
                                                                                    onClick={() => showFloorModal(floor)}
                                                                                >
                                                                                    Edit
                                                                                </button>
                                                                                <button
                                                                                    type="button"
                                                                                    className="secondary-btn"
                                                                                    style={DANGER_ACTION_STYLE}
                                                                                    onClick={() => {
                                                                                        if (window.confirm('Delete this floor and its rooms?')) {
                                                                                            handleDelete(
                                                                                                () =>
                                                                                                    canEdit
                                                                                                        ? pgApi.deleteFloor(floor.id)
                                                                                                        : pgApi.deleteUserFloor(selectedUser.id, floor.id),
                                                                                                'Floor deleted'
                                                                                            );
                                                                                        }
                                                                                    }}
                                                                                >
                                                                                    Delete
                                                                                </button>
                                                                            </div>
                                                                        </header>

                                                                        <div style={{ marginTop: '0.5rem', display: 'flex', justifyContent: 'space-between' }}>
                                                                            <span>Rooms ({rooms.length})</span>
                                                                            <button
                                                                                type="button"
                                                                                className="secondary-btn"
                                                                                onClick={() => showRoomModal(null, floor.id)}
                                                                            >
                                                                                + Add Room
                                                                            </button>
                                                                        </div>

                                                                        {rooms.length === 0 ? (
                                                                            <p style={{ color: 'var(--color-text-muted)' }}>No rooms defined.</p>
                                                                        ) : (
                                                                            <div style={{ display: 'grid', gap: '0.5rem', marginTop: '0.5rem' }}>
                                                                                {rooms.map((room) => (
                                                                                    <div
                                                                                        key={room.id}
                                                                                        style={{
                                                                                            display: 'flex',
                                                                                            justifyContent: 'space-between',
                                                                                            alignItems: 'center',
                                                                                            border: '1px solid var(--color-glass-border)',
                                                                                            borderRadius: '16px',
                                                                                            padding: '0.75rem 1rem',
                                                                                            background: 'var(--color-surface)',
                                                                                            boxShadow: 'var(--shadow-soft)',
                                                                                            color: 'var(--color-text)',
                                                                                        }}
                                                                                    >
                                                                                        <div>
                                                                                            <strong>{room.room_name}</strong>
                                                                                            {room.description ? (
                                                                                                <div style={{ color: 'var(--color-text-muted)', fontSize: '0.85rem' }}>{room.description}</div>
                                                                                            ) : null}
                                                                                        </div>
                                                                                        <div style={{ display: 'flex', gap: '0.5rem' }}>
                                                                                            <button
                                                                                                type="button"
                                                                                                className="secondary-btn"
                                                                                                onClick={() => showRoomModal(room)}
                                                                                            >
                                                                                                Edit
                                                                                            </button>
                                                                                                <button
                                                                                                    type="button"
                                                                                                    className="secondary-btn"
                                                                                                    style={DANGER_ACTION_STYLE}
                                                                                                    onClick={() => {
                                                                                                    if (window.confirm('Delete this room?')) {
                                                                                                        handleDelete(
                                                                                                            () =>
                                                                                                                canEdit
                                                                                                                    ? pgApi.deleteRoom(room.id)
                                                                                                                    : pgApi.deleteUserRoom(selectedUser.id, room.id),
                                                                                                            'Room deleted'
                                                                                                        );
                                                                                                    }
                                                                                                }}
                                                                                            >
                                                                                                Delete
                                                                                            </button>
                                                                                        </div>
                                                                                    </div>
                                                                                ))}
                                                                            </div>
                                                                        )}
                                                                    </section>
                                                                );
                                                            })}
                                                        </div>
                                                    )}
                                                </article>
                                            );
                                        })}
                                    </div>
                                )}
                            </section>
                        )}

                        {activeTab === 'networking' && (
                            <section style={{ display: 'grid', gap: '1rem' }}>
                                <section style={PANEL_WITH_PADDING}>
                                    <header style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <h3 style={{ margin: 0 }}>Router</h3>
                                        <div style={{ display: 'flex', gap: '0.5rem' }}>
                                            <button type="button" className="secondary-btn" onClick={() => showRouterModal(selectedRouter)}>
                                                {selectedRouter ? 'Edit Router' : 'Add Router'}
                                            </button>
                                            {selectedRouter ? (
                                    <button
                                        type="button"
                                        className="secondary-btn"
                                        style={DANGER_ACTION_STYLE}
                                        onClick={() => {
                                                        if (window.confirm('Delete router?')) {
                                                            handleDelete(
                                                                () =>
                                                                    canEdit
                                                                        ? pgApi.deleteRouter(selectedRouter.id)
                                                                        : pgApi.deleteUserRouter(selectedUser.id, selectedRouter.id),
                                                                'Router deleted'
                                                            );
                                                        }
                                                    }}
                                                >
                                                    Delete
                                                </button>
                                            ) : null}
                                        </div>
                                    </header>
                                    {selectedRouter ? (
                                        <dl style={{ display: 'grid', gridTemplateColumns: '140px 1fr', gap: '0.5rem' }}>
                                            <dt>Public IP</dt>
                                            <dd>{selectedRouter.public_ip}</dd>
                                            <dt>Gateway IP</dt>
                                            <dd>{selectedRouter.gateway_ip}</dd>
                                            <dt>Modem</dt>
                                            <dd>{selectedRouter.modem_type}</dd>
                                            <dt>Room ID</dt>
                                            <dd>{selectedRouter.location_room_id}</dd>
                                        </dl>
                                    ) : (
                                        <p style={{ color: 'var(--color-text-muted)' }}>No router configured.</p>
                                    )}
                                </section>

                                <section style={PANEL_WITH_PADDING}>
                                    <header style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <h3 style={{ margin: 0 }}>Access Points</h3>
                                        <button
                                            type="button"
                                            className="secondary-btn"
                                            onClick={() => showAccessPointModal(null)}
                                            disabled={!selectedRouter}
                                        >
                                            + Add Access Point
                                        </button>
                                    </header>
                                    {selectedAps.length === 0 ? (
                                        <p style={{ color: 'var(--color-text-muted)' }}>No access points associated with this user.</p>
                                    ) : (
                                        <div style={{ display: 'grid', gap: '0.75rem', marginTop: '0.75rem' }}>
                                            {selectedAps.map((ap) => {
                                                const radios = radiosByAp[ap.id] ?? [];
                                                return (
                                                    <article key={ap.id} style={BADGE_SURFACE_STYLE}>
                                                        <header style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                            <div>
                                                                <strong>Access Point #{ap.id}</strong>
                                                                <div style={{ color: 'var(--color-text-muted)', fontSize: '0.85rem' }}>Room {ap.location_room_id}</div>
                                                            </div>
                                                            <div style={{ display: 'flex', gap: '0.5rem' }}>
                                                                <button
                                                                    type="button"
                                                                    className="secondary-btn"
                                                                    onClick={() => showAccessPointModal(ap)}
                                                                >
                                                                    Edit
                                                                </button>
                                                                <button
                                                                    type="button"
                                                                    className="secondary-btn"
                                                                    style={DANGER_ACTION_STYLE}
                                                                    onClick={() => {
                                                                        if (window.confirm('Delete this access point?')) {
                                                                            handleDelete(
                                                                                () =>
                                                                                    canEdit
                                                                                        ? pgApi.deleteAccessPoint(ap.id)
                                                                                        : pgApi.deleteUserAccessPoint(selectedUser.id, ap.id),
                                                                                'Access point deleted'
                                                                            );
                                                                        }
                                                                    }}
                                                                >
                                                                    Delete
                                                                </button>
                                                            </div>
                                                        </header>

                                                        <div style={{ marginTop: '0.5rem', display: 'flex', justifyContent: 'space-between' }}>
                                                            <strong>Radios ({radios.length})</strong>
                                                            <button
                                                                type="button"
                                                                className="secondary-btn"
                                                                onClick={() => showRadioModal(ap, null)}
                                                            >
                                                                + Add Radio
                                                            </button>
                                                        </div>

                                                        {radios.length === 0 ? (
                                                            <p style={{ color: 'var(--color-text-muted)' }}>No radios configured.</p>
                                                        ) : (
                                                            <table style={{ width: '100%', marginTop: '0.5rem', borderCollapse: 'collapse' }}>
                                                                <thead>
                                                                    <tr>
                                                                        <th style={{ textAlign: 'left', padding: '0.25rem' }}>SSID</th>
                                                                        <th style={{ textAlign: 'left', padding: '0.25rem' }}>BSSID</th>
                                                                        <th style={{ textAlign: 'left', padding: '0.25rem' }}>Band</th>
                                                                        <th style={{ textAlign: 'left', padding: '0.25rem' }}>Actions</th>
                                                                    </tr>
                                                                </thead>
                                                                <tbody>
                                                                    {radios.map((radio) => (
                                                                        <tr key={radio.id}>
                                                                            <td style={{ padding: '0.25rem' }}>{radio.ssid}</td>
                                                                            <td style={{ padding: '0.25rem' }}>{radio.bssid}</td>
                                                                            <td style={{ padding: '0.25rem' }}>{radio.frequency_band}</td>
                                                                            <td style={{ padding: '0.25rem' }}>
                                                                                <button
                                                                                    type="button"
                                                                                    className="secondary-btn"
                                                                                    onClick={() => showRadioModal(ap, radio)}
                                                                                >
                                                                                    Edit
                                                                                </button>
                                                                                <button
                                                                                    type="button"
                                                                                    className="secondary-btn"
                                                                                    style={{ ...DANGER_ACTION_STYLE, marginLeft: '0.5rem' }}
                                                                                    onClick={() => {
                                                                                        if (window.confirm('Delete this radio?')) {
                                                                                            handleDelete(
                                                                                                () =>
                                                                                                    canEdit
                                                                                                        ? pgApi.deleteRadio(radio.id)
                                                                                                        : pgApi.deleteUserRadio(selectedUser.id, radio.id),
                                                                                                'Radio deleted'
                                                                                            );
                                                                                        }
                                                                                    }}
                                                                                >
                                                                                    Delete
                                                                                </button>
                                                                            </td>
                                                                        </tr>
                                                                    ))}
                                                                </tbody>
                                                            </table>
                                                        )}
                                                    </article>
                                                );
                                            })}
                                        </div>
                                    )}
                                </section>
                            </section>
                        )}

                        {activeTab === 'devices' && (
                            <section style={{ display: 'grid', gap: '1rem' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <h3 style={{ margin: 0 }}>Devices</h3>
                                    <button type="button" className="primary-btn" onClick={() => showDeviceModal(null)}>
                                        + Add Device
                                    </button>
                                </div>

                                {selectedDevices.length === 0 ? (
                                    <p style={{ color: 'var(--color-text-muted)' }}>No devices registered for this user.</p>
                                ) : (
                                    <div style={TABLE_CONTAINER_STYLE}>
                                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                            <thead style={{ background: 'var(--color-surface)' }}>
                                                <tr>
                                                    <th style={{ textAlign: 'left', padding: '0.5rem' }}>Device</th>
                                                    <th style={{ textAlign: 'left', padding: '0.5rem' }}>Room</th>
                                                    <th style={{ textAlign: 'left', padding: '0.5rem' }}>Provision</th>
                                                    <th style={{ textAlign: 'left', padding: '0.5rem' }}>Deployment</th>
                                                    <th style={{ textAlign: 'left', padding: '0.5rem' }}>Actions</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {selectedDevices.map((device) => {
                                                    const provision = provisionsByDevice[device.id];
                                                    const deployment = deploymentsByDevice[device.id];
                                                    return (
                                                        <tr key={device.id}>
                                                            <td style={{ padding: '0.5rem' }}>
                                                                <div style={{ fontWeight: 600 }}>{device.device_name}</div>
                                                                <div style={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>{device.mac}</div>
                                                            </td>
                                                            <td style={{ padding: '0.5rem' }}>{device.location_room_id}</td>
                                                            <td style={{ padding: '0.5rem' }}>
                                                                {provision ? (
                                                                    <div>
                                                                        <span
                                                                            style={{
                                                                                display: 'inline-block',
                                                                                padding: '0.2rem 0.6rem',
                                                                                borderRadius: '999px',
                                                                                background: provision.is_provisioned
                                                                                    ? STATUS_SUCCESS_BACKGROUND
                                                                                    : STATUS_DANGER_BACKGROUND,
                                                                                color: provision.is_provisioned
                                                                                    ? 'var(--color-success)'
                                                                                    : 'var(--color-danger)',
                                                                                fontSize: '0.75rem',
                                                                                letterSpacing: '0.12em',
                                                                                textTransform: 'uppercase',
                                                                            }}
                                                                        >
                                                                            {provision.is_provisioned ? 'Provisioned' : 'Pending'}
                                                                        </span>
                                                                        <div style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)' }}>
                                                                            {formatDate(provision.provisioned_date)}
                                                                        </div>
                                                                    </div>
                                                                ) : (
                                                                    <span style={{ color: 'var(--color-text-muted)', fontSize: '0.85rem' }}>Not provisioned</span>
                                                                )}
                                                            </td>
                                                            <td style={{ padding: '0.5rem' }}>
                                                                {deployment ? (
                                                                    <div>
                                                                        <span
                                                                            style={{
                                                                                display: 'inline-block',
                                                                                padding: '0.2rem 0.6rem',
                                                                                borderRadius: '999px',
                                                                                background: deployment.is_deployed
                                                                                    ? STATUS_ACCENT_BACKGROUND
                                                                                    : STATUS_DANGER_BACKGROUND,
                                                                                color: deployment.is_deployed
                                                                                    ? 'var(--color-accent)'
                                                                                    : 'var(--color-danger)',
                                                                                fontSize: '0.75rem',
                                                                                letterSpacing: '0.12em',
                                                                                textTransform: 'uppercase',
                                                                            }}
                                                                        >
                                                                            {deployment.is_deployed ? 'Deployed' : 'Inactive'}
                                                                        </span>
                                                                        <div style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)' }}>
                                                                            Start: {formatDate(deployment.start_date)}
                                                                        </div>
                                                                        <div style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)' }}>
                                                                            End: {formatDate(deployment.end_date)}
                                                                        </div>
                                                                    </div>
                                                                ) : (
                                                                    <span style={{ color: 'var(--color-text-muted)', fontSize: '0.85rem' }}>Not deployed</span>
                                                                )}
                                                            </td>
                                                            <td style={{ padding: '0.5rem' }}>
                                                                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem' }}>
                                                                    <button
                                                                        type="button"
                                                                        className="secondary-btn"
                                                                        onClick={() => showDeviceModal(device)}
                                                                    >
                                                                        Edit
                                                                    </button>
                                                                    <button
                                                                        type="button"
                                                                        className="secondary-btn"
                                                                        onClick={() => showProvisionModal(device, provision)}
                                                                    >
                                                                        {provision ? 'Edit Provision' : 'Add Provision'}
                                                                    </button>
                                                                    <button
                                                                        type="button"
                                                                        className="secondary-btn"
                                                                        onClick={() => showDeploymentModal(device, deployment)}
                                                                    >
                                                                        {deployment ? 'Edit Deployment' : 'Add Deployment'}
                                                                    </button>
                                <button
                                    type="button"
                                    className="secondary-btn"
                                    style={DANGER_ACTION_STYLE}
                                    onClick={() => {
                                                                            if (window.confirm('Delete this device and related records?')) {
                                                                                handleDelete(
                                                                                    () =>
                                                                                        canEdit
                                                                                            ? pgApi.deleteDevice(device.id)
                                                                                            : pgApi.deleteUserDevice(selectedUser.id, device.id),
                                                                                    'Device deleted'
                                                                                );
                                                                            }
                                                                        }}
                                                                    >
                                                                        Delete
                                                                    </button>
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </section>
                        )}
                    </div>
                ) : (
                    <p style={{ color: 'var(--color-text-muted)' }}>Select a user to manage their hierarchy.</p>
                )}
            </div>

            <EntityModal
                open={Boolean(modalConfig)}
                title={modalConfig?.title || ''}
                description={modalConfig?.description}
                fields={modalConfig?.fields || []}
                initialValues={modalConfig?.initialValues}
                onCancel={closeModal}
                onSubmit={modalConfig?.onSubmit || (async () => {})}
                submitLabel={modalConfig?.submitLabel}
            />
        </div>
    );
}

export default PgUsersDashboard;
